'use client';

import { SwiperSlide } from 'swiper/react';
import type { HeroSlideViewModel } from '@/lib/home-view-model';
import { sanitizeHeroDesign, type HeroDesign } from '@/lib/theme-settings';
import { HeroCarouselShell } from '@/themes/legacy/hero/HeroCarouselShell';
import { heroSlideStyles } from '@/themes/legacy/hero/heroSlideStyles';
import './theme02-hero.css';

type Theme02HeroProps = {
  slides: HeroSlideViewModel[];
  design: HeroDesign;
};

/** Marble.Theme.02 hero: the wide carousel — full-bleed background image (no outer card framing), text safe-area on the left. */
export function Theme02Hero({ slides, design: designProp }: Theme02HeroProps) {
  const design = sanitizeHeroDesign(designProp);

  return (
    <HeroCarouselShell variantClassName="hero-carousel--wide" design={design}>
      {slides.map((slide) => (
        <SwiperSlide key={slide.id}>
          <Theme02HeroSlide slide={slide} design={design} />
        </SwiperSlide>
      ))}
    </HeroCarouselShell>
  );
}

function Theme02HeroSlide({ slide, design }: { slide: HeroSlideViewModel; design: HeroDesign }) {
  const { heightClass, titleStyle, descriptionStyle, subtitleStyle, ctaClass, ctaStyle } = heroSlideStyles(design);

  return (
    <div className={`relative w-full overflow-hidden min-h-[420px] sm:min-h-[460px] md:aspect-[16/9] md:min-h-0 lg:aspect-[3/1] lg:max-h-[720px]${heightClass}`}>
      {/* Background image covers the entire slide — never split into separate
          DOM regions for a single breakpoint. Below md, a dedicated mobile
          image (falling back to the desktop one) replaces it via a plain
          responsive show/hide toggle, so the desktop layer's focal-position
          shift never applies on mobile. */}
      <div
        className="absolute inset-0 bg-cover bg-center md:bg-[65%_center] lg:bg-[70%_center] hidden md:block"
        style={{ backgroundImage: `url(${slide.image})` }}
      />
      <div
        className="absolute inset-0 bg-cover bg-center md:hidden"
        style={{ backgroundImage: `url(${slide.mobileImage || slide.image})` }}
      />

      {/* Subtle left-weighted legibility overlay — no solid panel/card. */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/15 to-transparent" />

      {/* Text safe-area: full-width on mobile, left 40% on tablet, left 1/3 on desktop. */}
      <div className="relative h-full flex flex-col justify-center px-6 py-10 sm:px-8 md:w-[40%] md:px-10 lg:w-1/3 lg:px-[clamp(1.5rem,5vw,4rem)]">
        {slide.subtitle && (
          <p className="text-white/90 font-semibold text-xs sm:text-sm mb-2 md:mb-3 tracking-wider uppercase animate-fade-in" style={subtitleStyle}>
            {slide.subtitle}
          </p>
        )}

        <h1
          className="text-2xl sm:text-3xl md:text-4xl lg:text-[2.75rem] font-bold text-white mb-3 md:mb-4 leading-tight animate-fade-in"
          style={titleStyle('100ms')}
        >
          {slide.title}
        </h1>

        {slide.description && (
          <p
            className="text-white/85 text-sm sm:text-base mb-6 md:mb-8 max-w-md animate-fade-in"
            style={descriptionStyle('200ms')}
          >
            {slide.description}
          </p>
        )}

        {slide.cta && (
          <a
            href={slide.cta.link}
            className={`inline-flex w-fit items-center px-6 py-3 md:px-8 md:py-3.5 bg-primary text-white font-semibold text-sm md:text-base rounded-lg hover:bg-primary-dark transition-all duration-200 shadow-2 hover:shadow-3 hover:-translate-y-1 animate-fade-in${ctaClass}`}
            style={ctaStyle('300ms')}
          >
            {slide.cta.text}
            <span className="ml-2">→</span>
          </a>
        )}
      </div>
    </div>
  );
}
